import { hasConsent } from './consent';

/**
 * The visitor id: one stable pseudonymous id per browser, for the people who
 * have not registered yet.
 *
 * Signed-out product views and shop clicks used to go nowhere at all, so the
 * admin saw nothing of what an unregistered visitor did. They now post to the
 * Worker's public `/api/visitors/events` carrying this id, and every sign-in
 * request sends the same id so the Worker can claim those rows onto the
 * account the person creates later.
 *
 * Two stores, on purpose. localStorage is the primary; a cookie is the
 * fallback, so clearing one of them does not hand the same person a new
 * identity and orphan everything they did before. Whichever store still holds
 * a valid id wins, and the other is rewritten from it.
 *
 * Consent gates everything. With analytics consent not given, this returns
 * null, mints nothing, writes nothing, and the callers send nothing.
 *
 * Never throws. In a private window where both stores are unavailable it
 * returns null rather than minting an id that would change on every call,
 * because an unstable id is worse than no id: it would scatter one person's
 * activity across many visitors and could never be claimed.
 */

const STORAGE_KEY = 'cch_visitor_id';
const COOKIE_KEY = 'cch_vid';
const COOKIE_MAX_AGE_DAYS = 400;

/** UUID v4, the shape the Worker validates. Anything else is regenerated. */
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValid(id: string | null | undefined): id is string {
  return typeof id === 'string' && UUID_V4.test(id);
}

function readLocalStorage(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLocalStorage(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Private mode or storage full. The cookie is the fallback.
  }
}

function readCookie(): string | null {
  try {
    const match = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${COOKIE_KEY}=`));
    return match ? decodeURIComponent(match.slice(COOKIE_KEY.length + 1)) : null;
  } catch {
    return null;
  }
}

function writeCookie(id: string): void {
  try {
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_KEY}=${id}; max-age=${maxAge}; SameSite=Lax; path=/${secure}`;
  } catch {
    // Cookies blocked. localStorage is the fallback.
  }
}

function mint(): string | null {
  try {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
    // Older Safari has getRandomValues but no randomUUID.
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return null;
  }
}

function consentGiven(): boolean {
  try {
    return hasConsent();
  } catch {
    return false;
  }
}

/**
 * The visitor id for this browser, or null when consent is not given or no
 * store will hold one. Created lazily on the first call that is allowed to
 * create it.
 */
export function getVisitorId(): string | null {
  if (!consentGiven()) return null;

  const stored = readLocalStorage();
  const cookie = readCookie();

  let id: string | null = null;
  if (isValid(stored)) id = stored;
  else if (isValid(cookie)) id = cookie;
  else id = mint();

  if (!isValid(id)) return null;

  // Keep both stores in step, so clearing either one restores from the other.
  if (stored !== id) writeLocalStorage(id);
  if (cookie !== id) writeCookie(id);

  // Prove it will survive this page. If neither store kept it, the next call
  // would mint a different id, and a changing id is worse than none.
  if (readLocalStorage() !== id && readCookie() !== id) return null;

  return id;
}

/** Test seam: the keys both stores use. */
export const VISITOR_ID_KEYS = {
  storage: STORAGE_KEY,
  cookie: COOKIE_KEY,
} as const;
